import {
  Button,
  color,
  fontWeight,
  getComponentColors,
  getInputFieldBorderAndColorStyles,
  IconChevronDown,
  styled,
  TextElement,
} from '@lessonup/ui-components';
import React, { useRef } from 'react';
import { useBoolean, useOnClickOutside } from 'usehooks-ts';
import { DropDown, StyleDropdownOption } from './CustomSelectDropdown';
import { useCustomSelectScrollIntoView } from './useCustomSelectScrollIntoView';

export type PickerWidth = 'fit' | 'full';
export interface NoFocusPickerProps<T extends string | undefined> {
  value: T | T[] | undefined;
  valueLabel?: string;
  options: { label: string; value: T }[];
  onChange: (value: T) => void;
  styleOption?: StyleDropdownOption<T>;
  multiple?: boolean;
}

export function NoFocusPicker<T extends string | undefined>({
  value,
  valueLabel,
  options: options,
  onChange,
  styleOption,
  multiple = false,
}: NoFocusPickerProps<T>) {
  const isOpen = useBoolean(false);
  const containerRef = useRef<HTMLInputElement | null>(null);

  const customScroll = useCustomSelectScrollIntoView();

  const handleClose = () => {
    isOpen.setFalse();
    customScroll.resetScrollIntoView();
  };

  // TODO: TECH-180: fix the ref type issue
  useOnClickOutside(containerRef as never, () => {
    handleClose();
  });

  const handlePickOption = (event: React.MouseEvent<HTMLElement, MouseEvent>, value: T) => {
    event.preventDefault();
    onChange(value);
    if (!multiple) {
      handleClose();
    }
  };

  const toggleDropdown = (event: React.MouseEvent<HTMLElement> | React.TouchEvent<HTMLElement>) => {
    event.preventDefault();
    if (isOpen.value) {
      handleClose();
    } else {
      isOpen.setTrue();
    }
  };

  return (
    <SelectContainer ref={containerRef} isOpen={isOpen.value}>
      <FakeInputFieldButton
        buttonType="secondary"
        onMouseDown={toggleDropdown}
        onTouchStart={toggleDropdown}
        iconEnd={<IconChevronDown />}
      >
        <TextElement textStyle="label">{valueLabel ? valueLabel : value}</TextElement>
      </FakeInputFieldButton>
      {isOpen.value && DropDown<T>(customScroll, options, handlePickOption, value, styleOption)}
    </SelectContainer>
  );
}

const FakeInputFieldButton = styled(Button)`
  ${getInputFieldBorderAndColorStyles(getComponentColors('neutral'), 'medium')};
  width: 100%;
  justify-content: space-between;
  & > span {
    font-weight: ${fontWeight.regular};
    width: 100%;
    justify-content: space-between;
  }
`;

const SelectContainer = styled.div<{ isOpen: boolean }>`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  & > button {
    ${(props) => (props.isOpen ? `outline: 2px solid ${color.utils.focus.stroke}` : '')};
  }
`;
